import {
  AlertRule,
  TriggerEntityType,
  TriggerType,
} from './../../../models/alert-rule.model';
import { TranslateService } from '@ngx-translate/core';
import { GroupRole } from '../../../models/user-group-member.model';
import { Injectable } from '@angular/core';

/**
 * Helper class handling various localization tasks.
 */
@Injectable()
export class LocalizationHelper {
  constructor(private translate: TranslateService) {}

  public localizedNameForPermission(permissionType: string): string {
    switch (permissionType) {
      case 'administrate-group':
        return this.translate.instant('permission.administrate-group.name');
      case 'view-station':
        return this.translate.instant('permission.view-station.name');
      case 'manage-station':
        return this.translate.instant('permission.manage-station.name');
      case 'view-ocpp-configuration-url':
        return this.translate.instant(
          'permission.view-ocpp-configuration-url.name'
        );
      case 'generate-qr-codes':
        return this.translate.instant('permission.generate-qr-codes.name');
      case 'access-permissions-feature':
        return this.translate.instant(
          'permission.access-permissions-feature.name'
        );
      case 'access-rocketmaster-feature':
        return this.translate.instant(
          'permission.access-rocketmaster-feature.name'
        );
      case 'access-station-maintenance-feature':
        return this.translate.instant(
          'permission.access-station-maintenance-feature.name'
        );
      case 'access-reports-feature':
        return this.translate.instant('permission.access-reports-feature.name');
      case 'access-load-management-feature':
        return this.translate.instant(
          'permission.access-load-management-feature.name'
        );
      case 'access-autoexport-feature':
        return this.translate.instant(
          'permission.access-autoexport-feature.name'
        );
      case 'access-hubject-feature':
        return this.translate.instant('permission.access-hubject-feature.name');
      case 'access-auto-payout-feature':
        return this.translate.instant('access-auto-payout-feature.name');
      case 'access-price-plans-feature':
        return this.translate.instant(
          'permission.access-price-plans-feature.name'
        );
      default:
        return permissionType;
    }
  }

  public localizedDescriptionForPermission(permissionType: string): string {
    switch (permissionType) {
      case 'administrate-group':
        return this.translate.instant(
          'permission.administrate-group.description'
        );
      case 'view-station':
        return this.translate.instant('permission.view-station.description');
      case 'manage-station':
        return this.translate.instant('permission.manage-station.description');
      case 'view-ocpp-configuration-url':
        return this.translate.instant(
          'permission.view-ocpp-configuration-url.description'
        );
      case 'generate-qr-codes':
        return this.translate.instant(
          'permission.generate-qr-codes.description'
        );
      case 'access-permissions-feature':
        return this.translate.instant(
          'permission.access-permissions-feature.description'
        );
      case 'access-rocketmaster-feature':
        return this.translate.instant(
          'permission.access-rocketmaster-feature.description'
        );
      case 'access-station-maintenance-feature':
        return this.translate.instant(
          'permission.access-station-maintenance-feature.description'
        );
      case 'access-reports-feature':
        return this.translate.instant(
          'permission.access-reports-feature.description'
        );
      case 'access-load-management-feature':
        return this.translate.instant(
          'permission.access-load-management-feature.description'
        );
      case 'access-autoexport-feature':
        return this.translate.instant(
          'permission.access-autoexport-feature.description'
        );
      case 'access-hubject-feature':
        return this.translate.instant(
          'permission.access-hubject-feature.description'
        );
      case 'access-auto-payout-feature':
        return this.translate.instant('access-auto-payout-feature.description');
      case 'access-price-plans-feature':
        return this.translate.instant(
          'permission.access-price-plans-feature.description'
        );
      default:
        return '-';
    }
  }

  public localizedAssigneeTypeForPermission(assigneeType: string): string {
    switch (assigneeType) {
      case 'user':
        return this.translate.instant('permission.assignee.user.name');
      case 'user-group':
        return this.translate.instant('permission.assignee.user-group.name');
      default:
        return '-';
    }
  }

  public localizedRoleName(role: GroupRole): string {
    switch (role) {
      case GroupRole.Admin:
        return this.translate.instant('group-role.admin');
      case GroupRole.Member:
        return this.translate.instant('group-role.member');
      default:
        return '-';
    }
  }

  public localizedNameForFeature(type: string): string {
    switch (type) {
      case 'access-rocketmaster-feature':
        return this.translate.instant(
          'feature.access-rocketmaster-feature.name'
        );
      case 'access-station-maintenance-feature':
        return this.translate.instant(
          'feature.access-station-maintenance-feature.name'
        );
      case 'access-reports-feature':
        return this.translate.instant('feature.access-reports-feature.name');
      case 'access-load-management-feature':
        return this.translate.instant(
          'feature.access-load-management-feature.name'
        );
      case 'access-permissions-feature':
        return this.translate.instant(
          'feature.access-permissions-feature.name'
        );
      case 'access-autoexport-feature':
        return this.translate.instant('feature.access-autoexport-feature.name');
      case 'advanced-user-management-feature':
        return this.translate.instant(
          'feature.advanced-user-management-feature.name'
        );
      case 'access-hubject-feature':
        return this.translate.instant('feature.access-hubject-feature.name');
      case 'access-auto-payout-feature':
        return this.translate.instant('access-auto-payout-feature.name');
      case 'access-price-plans-feature':
        return this.translate.instant(
          'feature.access-price-plans-feature.name'
        );
      default:
        return null;
    }
  }

  public localizedDescriptionForFeature(type: string): string {
    switch (type) {
      case 'access-rocketmaster-feature':
        return this.translate.instant(
          'feature.access-rocketmaster-feature.description'
        );
      case 'access-station-maintenance-feature':
        return this.translate.instant(
          'feature.access-station-maintenance-feature.description'
        );
      case 'access-reports-feature':
        return this.translate.instant(
          'feature.access-reports-feature.description'
        );
      case 'access-load-management-feature':
        return this.translate.instant(
          'feature.access-load-management-feature.description'
        );
      case 'access-permissions-feature':
        return this.translate.instant(
          'feature.access-permissions-feature.description'
        );
      case 'access-autoexport-feature':
        return this.translate.instant(
          'feature.access-autoexport-feature.description'
        );
      case 'advanced-user-management-feature':
        return this.translate.instant(
          'feature.advanced-user-management-feature.description'
        );
      case 'access-hubject-feature':
        return this.translate.instant(
          'feature.access-hubject-feature.description'
        );
      case 'access-auto-payout-feature':
        return this.translate.instant('access-auto-payout-feature.description');
      case 'access-price-plans-feature':
        return this.translate.instant(
          'feature.access-price-plans-feature.description'
        );
      default:
        return null;
    }
  }

  public localizedMessageForApiErrorCode(code: string): string {
    switch (code) {
      case 'bad_request':
        return this.translate.instant('error.message.bad-request');
      case 'unauthorized':
        return this.translate.instant('error.message.unauthorized');
      case 'user_not_found':
        return this.translate.instant('error.message.user-not-found');
      case 'user_group_not_found':
        return this.translate.instant('error.message.user-group-not-found');
      case 'station_not_found':
        return this.translate.instant('error.message.station-not-found');
      case 'outstanding_payment':
        return this.translate.instant('error.message.outstanding-payment');
      case 'payment_validation_failed':
        return this.translate.instant(
          'error.message.payment-validation-failed'
        );
      case 'station_group_not_found':
        return this.translate.instant('error.message.station-group-not-found');
      case 'connector_not_found':
        return this.translate.instant('error.message.connector-not-found');
      case 'unauthorized_payments':
        return this.translate.instant('error.message.unauthorized-payments');
      case 'rfid_tag_taken':
        return this.translate.instant('error.message.rfid-tag-taken');
      case 'cant_delete_station_location_hubject_enabled':
        return this.translate.instant(
          'error.message.cant-delete-station-location-hubject-enabled'
        );
      case 'account_already_exists':
        return this.translate.instant('error.message.account-already-exists');
      case 'card_terminal_not_found':
        return this.translate.instant('error.message.card-terminal-not-found');
      case 'invalid_registration_code':
        return this.translate.instant(
          'error.message.invalid-registration-code'
        );
      case 'card_terminal_provider_unavailable':
        return this.translate.instant(
          'error.message.card-terminal-provider-unavailable'
        );
      case 'not_station_manager':
        return this.translate.instant('error.message.not-station-manager');
      case 'not_station_group_administrator':
        return this.translate.instant(
          'error.message.not-station-group-administrator'
        );
      case 'invalid_email':
        return this.translate.instant('error.message.invalid-email');
      case 'invalid_password':
        return this.translate.instant('error.message.invalid-password');
      case 'payout_too_low':
        return this.translate.instant('error.message.payout-too-low');
      default:
        return this.translate.instant('error.message.unknown');
    }
  }

  public localizedNameForReportFormatType(type: string): string {
    switch (type) {
      case 'pdf':
        return this.translate.instant('reports.report-type.pdf');
      case 'csv':
        return this.translate.instant('reports.report-type.csv');
      case 'csv-extended':
        return this.translate.instant('reports.report-type.csv-extended');
      default:
        return type;
    }
  }

  public localizedNameForPriceRuleProductType(type: string): string {
    switch (type) {
      case 'parking':
        return this.translate.instant('price-rule.product-type.parking.name');
      case 'energy':
        return this.translate.instant('price-rule.product-type.energy.name');
      default:
        return type;
    }
  }

  public localizedUnitForPriceRuleProductType(type: string): string {
    switch (type) {
      case 'parking':
        return this.translate.instant('price-rule.product-type.parking.unit');
      case 'energy':
        return this.translate.instant('price-rule.product-type.energy.unit');
      default:
        return type;
    }
  }

  public localizedDateStringFromTimestamp(timestamp: number): string {
    return new Date(timestamp * 1000).toLocaleDateString('de-ch');
  }

  public localizedNameForAlertRuleTriggerType(type: TriggerType): string {
    switch (type) {
      case TriggerType.ChargingStarted:
        return this.translate.instant(
          'alert-rule.trigger-type.charging-started.name'
        );
      case TriggerType.ChargingStopped:
        return this.translate.instant(
          'alert-rule.trigger-type.charging-stopped.name'
        );
      case TriggerType.OcppConnectionAborted:
        return this.translate.instant(
          'alert-rule.trigger-type.ocpp-connection-aborted.name'
        );
      case TriggerType.OcppConnectionRegained:
        return this.translate.instant(
          'alert-rule.trigger-type.ocpp-connection-regained.name'
        );
      case TriggerType.StationFaulted:
        return this.translate.instant(
          'alert-rule.trigger-type.station-faulted.name'
        );
      case TriggerType.StationUnavailable:
        return this.translate.instant(
          'alert-rule.trigger-type.station-unavailable.name'
        );
      case TriggerType.StationAvailable:
        return this.translate.instant(
          'alert-rule.trigger-type.station-available.name'
        );
      default:
        return type;
    }
  }

  public localizedNameForAlertRuleEntityType(type: TriggerEntityType): string {
    switch (type) {
      case TriggerEntityType.Station:
        return this.translate.instant(
          'alert-rule.trigger-entity-type.station.name'
        );
      case TriggerEntityType.StationGroup:
        return this.translate.instant(
          'alert-rule.trigger-entity-type.station-group.name'
        );
      default:
        return type;
    }
  }

  public localizedDescriptionForAlertRule(rule: AlertRule) {
    return rule.name;
  }
}
